import { useEffect, useMemo, useRef, useState } from "react";
import type { ImperativePanelHandle } from "react-resizable-panels";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Header from "@/components/Header";
import MainWorkspaceLayout from "@/components/workspace/MainWorkspaceLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from "@/components/ui/resizable";
import { EntityListPanel } from "@/components/shared/EntityListPanel";
import { EntityDetailsShell } from "@/components/shared/EntityDetailsShell";
import { EntityFilesSection } from "@/components/shared/EntityFilesSection";
import { EntityGeometrySection } from "@/components/shared/EntityGeometrySection";
import { UdgspzWorkspaceMapPanel } from "@/components/udgspz/UdgspzWorkspaceMapPanel";
import { ChevronDown, Crosshair, ListFilter, MapPinned, Plus, Trash2 } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { udgspzEntitiesApi } from "@/services/udgspz-entities.service";
import { useToast } from "@/hooks/use-toast";
import { useIsMobile } from "@/hooks/use-mobile";
import { notifyModuleError, notifyModuleSuccess } from "@/lib/module-toast";
import { dispatchUdgspzHeatmapOverlay } from "@/lib/udgspz-heatmap-map-bridge";
import type { Geometry } from "@/types/api-v2";
import type { UdgspzMapGeometryItem } from "@/types/udgspz-entity";
import { UdgspzEntityMapStyleForm } from "@/components/udgspz/UdgspzEntityMapStyleForm";
import { SectionHelpTooltip } from "@/components/shared/SectionHelpTooltip";
import { ModuleRegistryTitle } from "@/components/shared/ModuleRegistryTitle";
import { MapStylePreviewChips } from "@/components/shared/MapStylePreviewChips";
import { UdgspzGeographyAttachDialog } from "@/components/udgspz/UdgspzGeographyAttachDialog";

export default function UdgspzWorkspacePage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState(() => searchParams.get("q") || "");
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [nameInput, setNameInput] = useState("");
  const [typeIdInput, setTypeIdInput] = useState<string>("none");
  const [parentIdInput, setParentIdInput] = useState<string>("none");
  const [createNameInput, setCreateNameInput] = useState("");
  const [createTypeIdInput, setCreateTypeIdInput] = useState<string>("none");
  const [createParentIdInput, setCreateParentIdInput] = useState<string>("none");
  const [bindGeometryName, setBindGeometryName] = useState("");
  const [bindGeometryId, setBindGeometryId] = useState("");
  const [geographyOpen, setGeographyOpen] = useState(false);
  /** Геометрия в форме стиля (editing). */
  const [selectedMapGeomId, setSelectedMapGeomId] = useState<number | null>(null);
  /** Подсветка на thematic overlay; null = чистый вид стиля. */
  const [mapHighlightGeomId, setMapHighlightGeomId] = useState<number | null>(null);
  const [entityStyleOpen, setEntityStyleOpen] = useState(false);
  const [udgMapDrawing, setUdgMapDrawing] = useState(false);
  const [hiddenContourTypeIds, setHiddenContourTypeIds] = useState<number[]>([]);
  const [resolveLon, setResolveLon] = useState("37.658583");
  const [resolveLat, setResolveLat] = useState("55.740095");
  const [linkGinfObjId, setLinkGinfObjId] = useState("");
  const [activeTab, setActiveTab] = useState("general");
  const [mapPreviewGeometry, setMapPreviewGeometry] = useState<Geometry | null>(null);
  const [mapPreviewNonce, setMapPreviewNonce] = useState(0);
  const [mapCollapsed, setMapCollapsed] = useState(false);
  const [deletedFilter, setDeletedFilter] = useState<"active" | "include" | "deleted_only">("active");
  const [heatmapEnabled, setHeatmapEnabled] = useState(false);
  const [validAtFrom, setValidAtFrom] = useState("");
  const [validAtTo, setValidAtTo] = useState("");
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [createValidFrom, setCreateValidFrom] = useState("");
  const [createValidTo, setCreateValidTo] = useState("");
  const [validFromInput, setValidFromInput] = useState("");
  const [validToInput, setValidToInput] = useState("");
  const mapPanelRef = useRef<ImperativePanelHandle>(null);
  const isMobile = useIsMobile();
  const listFiltersActive =
    deletedFilter !== "active" || Boolean(validAtFrom) || Boolean(validAtTo) || heatmapEnabled;
  const validityListFilter = useMemo(
    () => ({
      valid_at_from: validAtFrom || undefined,
      valid_at_to: validAtTo || undefined,
    }),
    [validAtFrom, validAtTo],
  );

  useEffect(() => {
    if (!isMobile) return;
    setMapCollapsed(true);
    mapPanelRef.current?.collapse();
  }, [isMobile]);

  const listQuery = useQuery({
    queryKey: ["udgspz-entities", "list", searchTerm, deletedFilter, validityListFilter],
    queryFn: () =>
      udgspzEntitiesApi.list({
        limit: 100,
        q: searchTerm || undefined,
        include_deleted: deletedFilter === "include",
        deleted_only: deletedFilter === "deleted_only",
        ...validityListFilter,
      }),
  });
  const heatmapQuery = useQuery({
    queryKey: ["udgspz-entities", "heatmap-points", validityListFilter],
    queryFn: () => udgspzEntitiesApi.getHeatmapPoints({ ...validityListFilter, limit: 5000 }),
    enabled: heatmapEnabled,
  });
  useEffect(() => {
    if (!heatmapEnabled) {
      dispatchUdgspzHeatmapOverlay(null);
      return;
    }
    if (heatmapQuery.data) {
      dispatchUdgspzHeatmapOverlay(heatmapQuery.data);
    }
  }, [heatmapEnabled, heatmapQuery.data]);
  useEffect(() => {
    return () => {
      dispatchUdgspzHeatmapOverlay(null);
    };
  }, []);
  const entities = listQuery.data?.items ?? [];
  const selectedEntity = useMemo(
    () => entities.find((item) => item.eudg_id === selectedId) ?? null,
    [entities, selectedId],
  );
  const isSelectedDeleted = Boolean(selectedEntity?.is_del);
  const detailQuery = useQuery({
    queryKey: ["udgspz-entities", "detail", selectedId],
    queryFn: () => udgspzEntitiesApi.getById(selectedId!),
    enabled: selectedId != null && !isSelectedDeleted,
    placeholderData: (previousData) => previousData,
  });
  const metaQuery = useQuery({
    queryKey: ["udgspz-entities", "meta"],
    queryFn: () => udgspzEntitiesApi.getMeta(),
  });
  const materialsQuery = useQuery({
    queryKey: ["udgspz-entities", "materials", selectedId],
    queryFn: () => udgspzEntitiesApi.listMaterials(selectedId!),
    enabled: selectedId != null && !isSelectedDeleted,
    placeholderData: (previousData) => previousData,
  });
  const mapGeometries = detailQuery.data?.map_geometries ?? [];
  useEffect(() => {
    setSelectedMapGeomId(null);
    setMapHighlightGeomId(null);
    setEntityStyleOpen(false);
  }, [selectedId]);
  useEffect(() => {
    if (selectedMapGeomId == null) return;
    if (!mapGeometries.some((item) => item.eudg_map_geom_id === selectedMapGeomId)) {
      setSelectedMapGeomId(null);
      setMapHighlightGeomId(null);
    }
  }, [mapGeometries, selectedMapGeomId]);
  useEffect(() => {
    if (mapHighlightGeomId == null) return;
    if (!mapGeometries.some((item) => item.eudg_map_geom_id === mapHighlightGeomId)) {
      setMapHighlightGeomId(null);
    }
  }, [mapGeometries, mapHighlightGeomId]);
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      if (mapHighlightGeomId != null) {
        event.preventDefault();
        setMapHighlightGeomId(null);
        return;
      }
      if (selectedMapGeomId != null) {
        event.preventDefault();
        setSelectedMapGeomId(null);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [mapHighlightGeomId, selectedMapGeomId]);
  const selectedMapGeom = useMemo(
    () => mapGeometries.find((item) => item.eudg_map_geom_id === selectedMapGeomId) ?? null,
    [mapGeometries, selectedMapGeomId],
  );
  const selectMapGeomForEdit = (eudgMapGeomId: number) => {
    setSelectedMapGeomId(eudgMapGeomId);
    setMapHighlightGeomId(eudgMapGeomId);
  };
  const toggleMapGeomSelection = (eudgMapGeomId: number) => {
    if (selectedMapGeomId !== eudgMapGeomId) {
      selectMapGeomForEdit(eudgMapGeomId);
      return;
    }
    setMapHighlightGeomId((current) => (current === eudgMapGeomId ? null : eudgMapGeomId));
  };
  const typeMapStyle = useMemo(
    () =>
      metaQuery.data?.type_map_styles?.find(
        (s) => s.kls_id === detailQuery.data?.kls_id_eudg_type,
      ),
    [metaQuery.data?.type_map_styles, detailQuery.data?.kls_id_eudg_type],
  );
  const entityDefaultFill =
    detailQuery.data?.map_fill_color ?? typeMapStyle?.fill_color ?? "#64748b";
  const entityDefaultLine =
    detailQuery.data?.map_line_color ?? typeMapStyle?.line_color ?? "#475569";
  const entityDefaultPattern =
    detailQuery.data?.map_pattern_code ?? typeMapStyle?.pattern_code ?? "solid";
  const entityDefaultWidth =
    detailQuery.data?.map_line_width ?? typeMapStyle?.line_width ?? 2;
  const entityDefaultDash =
    detailQuery.data?.map_line_dash_code ?? typeMapStyle?.line_dash_code ?? "solid";
  const entityDefaultLabelVisible =
    detailQuery.data?.map_label_visible ?? typeMapStyle?.label_visible ?? true;
  const entityDefaultLabelTextMode =
    detailQuery.data?.map_label_text_mode ?? typeMapStyle?.label_text_mode ?? "entity_name";
  const entityDefaultLabelText =
    detailQuery.data?.map_label_text ??
    (typeMapStyle?.label_text_mode === "custom" ? typeMapStyle.label_text : "") ??
    "";
  const entityDefaultLabelColor =
    detailQuery.data?.map_label_color ?? typeMapStyle?.label_color ?? "#0f172a";
  const entityDefaultLabelSize =
    detailQuery.data?.map_label_size ?? typeMapStyle?.label_size ?? 13;
  const entityDefaultLabelAnchor =
    detailQuery.data?.map_label_text_anchor ?? typeMapStyle?.label_text_anchor ?? "center";
  const entityDefaultLabelOffsetX =
    detailQuery.data?.map_label_offset_x ?? typeMapStyle?.label_offset_x ?? 0;
  const entityDefaultLabelOffsetY =
    detailQuery.data?.map_label_offset_y ?? typeMapStyle?.label_offset_y ?? 0;
  const entityDefaultLabelHaloColor =
    detailQuery.data?.map_label_halo_color ?? typeMapStyle?.label_halo_color ?? "#ffffff";
  const entityDefaultLabelHaloWidth =
    detailQuery.data?.map_label_halo_width ?? typeMapStyle?.label_halo_width ?? 2;
  const contourQuery = useQuery({
    queryKey: ["udgspz-entities", "map-contours"],
    queryFn: () => udgspzEntitiesApi.listMapContours(),
  });
  const cardQuery = useQuery({
    queryKey: ["udgspz-entities", "card", selectedId],
    queryFn: () => udgspzEntitiesApi.getCard(selectedId!),
    enabled: selectedId != null && !isSelectedDeleted,
    placeholderData: (previousData) => previousData,
  });
  const infraLinksQuery = useQuery({
    queryKey: ["udgspz-entities", "infra-links", selectedId],
    queryFn: () => udgspzEntitiesApi.listInfraLinks(selectedId!),
    enabled: selectedId != null && !isSelectedDeleted,
    placeholderData: (previousData) => previousData,
  });
  const countryLinksQuery = useQuery({
    queryKey: ["udgspz-entities", "country-links", selectedId],
    queryFn: () => udgspzEntitiesApi.listCountryLinks(selectedId!),
    enabled: selectedId != null && !isSelectedDeleted,
    placeholderData: (previousData) => previousData,
  });
  const [templateFieldDrafts, setTemplateFieldDrafts] = useState<Record<string, string>>({});
  useEffect(() => {
    if (!cardQuery.data?.field_values) return;
    const next: Record<string, string> = {};
    for (const fv of cardQuery.data.field_values) {
      next[fv.field_key] = fv.value == null ? "" : String(fv.value);
    }
    setTemplateFieldDrafts(next);
  }, [cardQuery.data]);
  const patchMutation = useMutation({
    mutationFn: async (payload: {
      eudg_name?: string | null;
      kls_id_eudg_type?: number | null;
      eudg_id_parent?: number | null;
      eudg_valid_from?: string | null;
      eudg_valid_to?: string | null;
    }) => {
      if (!selectedId) throw new Error("eudg_id is required");
      return udgspzEntitiesApi.patch(selectedId, payload);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["udgspz-entities", "detail", selectedId] });
      await queryClient.invalidateQueries({ queryKey: ["udgspz-entities", "list"] });
      await queryClient.invalidateQueries({ queryKey: ["udgspz-entities", "heatmap-points"] });
      notifyModuleSuccess(toast, "Карточка УДГСПЗ обновлена");
    },
    onError: (e: Error) => {
      notifyModuleError(toast, "обновление карточки УДГСПЗ", e);
    },
  });
  const putCardMutation = useMutation({
    mutationFn: async (values: Record<string, unknown>) => {
      if (!selectedId) throw new Error("eudg_id is required");
      return udgspzEntitiesApi.putCard(selectedId, { values });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["udgspz-entities", "card", selectedId] });
      notifyModuleSuccess(toast, "Поля шаблона сохранены");
    },
    onError: (e: Error) => {
      notifyModuleError(toast, "сохранение полей шаблона УДГСПЗ", e);
    },
  });
  const saveSketchMutation = useMutation({
    mutationFn: async (payload: { name: string; geojson: GeoJSON.Feature }) => {
      if (!selectedId) throw new Error("eudg_id is required");
      return udgspzEntitiesApi.createMapSketch(selectedId, payload);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["udgspz-entities", "detail", selectedId] });
      await queryClient.invalidateQueries({ queryKey: ["udgspz-entities", "list"] });
      await queryClient.invalidateQueries({ queryKey: ["udgspz-entities", "heatmap-points"] });
      await queryClient.invalidateQueries({ queryKey: ["udgspz-entities", "map-contours"] });
      notifyModuleSuccess(toast, "Контур сохранён");
    },
    onError: (e: Error) => {
      notifyModuleError(toast, "сохранение контура УДГСПЗ", e);
    },
  });
  const createMapGeometryMutation = useMutation({
    mutationFn: async (mapGeometryId: number) => {
      if (!selectedId) throw new Error("eudg_id is required");
      return udgspzEntitiesApi.createMapGeometry(selectedId, { map_geometry_id: mapGeometryId });
    },
    onSuccess: async () => {
      setBindGeometryId("");
      setBindGeometryName("");
      await queryClient.invalidateQueries({ queryKey: ["udgspz-entities", "detail", selectedId] });
      await queryClient.invalidateQueries({ queryKey: ["udgspz-entities", "list"] });
      await queryClient.invalidateQueries({ queryKey: ["udgspz-entities", "map-contours"] });
      notifyModuleSuccess(toast, "Геометрия привязана");
    },
    onError: (e: Error) => {
      notifyModuleError(toast, "привязку геометрии УДГСПЗ", e);
    },
  });
  const attachGeographyMutation = useMutation({
    mutationFn: async (geographyId: number) => {
      if (!selectedId) throw new Error("eudg_id is required");
      return udgspzEntitiesApi.createMapFromGeography(selectedId, geographyId);
    },
    onSuccess: async () => {
      setGeographyOpen(false);
      await queryClient.invalidateQueries({ queryKey: ["udgspz-entities", "detail", selectedId] });
      await queryClient.invalidateQueries({ queryKey: ["udgspz-entities", "list"] });
      await queryClient.invalidateQueries({ queryKey: ["udgspz-entities", "map-contours"] });
      notifyModuleSuccess(toast, "Граница добавлена в схему");
    },
    onError: (e: Error) => {
      notifyModuleError(toast, "добавление границы в схему УДГСПЗ", e);
    },
  });
  const deleteMapGeometryMutation = useMutation({
    mutationFn: async (eudgMapGeomId: number) => {
      if (!selectedId) throw new Error("eudg_id is required");
      return udgspzEntitiesApi.deleteMapGeometry(selectedId, eudgMapGeomId);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["udgspz-entities", "detail", selectedId] });
      await queryClient.invalidateQueries({ queryKey: ["udgspz-entities", "list"] });
      await queryClient.invalidateQueries({ queryKey: ["udgspz-entities", "map-contours"] });
      notifyModuleSuccess(toast, "Привязка геометрии удалена");
    },
    onError: (e: Error) => {
      notifyModuleError(toast, "удаление привязки геометрии УДГСПЗ", e);
    },
  });
  const resolveRegionMutation = useMutation({
    mutationFn: async () => {
      const longitude = Number(resolveLon);
      const latitude = Number(resolveLat);
      if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) {
        throw new Error("Укажите корректные longitude/latitude");
      }
      return udgspzEntitiesApi.resolveRegion({ longitude, latitude, limit: 50 });
    },
    onSuccess: () => {
      notifyModuleSuccess(toast, "Resolve-region выполнен");
    },
    onError: (e: Error) => {
      notifyModuleError(toast, "resolve-region УДГСПЗ", e);
    },
  });
  const refreshInfraLinksMutation = useMutation({
    mutationFn: async () => {
      if (!selectedId) throw new Error("eudg_id is required");
      return udgspzEntitiesApi.refreshInfraLinks(selectedId);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["udgspz-entities", "infra-links", selectedId] });
      notifyModuleSuccess(toast, "Infra-links обновлены");
    },
    onError: (e: Error) => notifyModuleError(toast, "refresh infra-links", e),
  });
  const createCountryLinkMutation = useMutation({
    mutationFn: async () => {
      if (!selectedId) throw new Error("eudg_id is required");
      const ginfObjId = Number(linkGinfObjId);
      if (!Number.isFinite(ginfObjId) || ginfObjId < 1) throw new Error("Укажите ginf_obj_id");
      return udgspzEntitiesApi.createCountryLink(selectedId, {
        ginf_obj_id: ginfObjId,
        coverage: "PARTIAL",
      });
    },
    onSuccess: async () => {
      setLinkGinfObjId("");
      await queryClient.invalidateQueries({ queryKey: ["udgspz-entities", "country-links", selectedId] });
      notifyModuleSuccess(toast, "Связь с ГО создана");
    },
    onError: (e: Error) => notifyModuleError(toast, "создание country-link", e),
  });
  const refreshCountryLinksMutation = useMutation({
    mutationFn: async () => {
      if (!selectedId) throw new Error("eudg_id is required");
      return udgspzEntitiesApi.refreshCountryLinks(selectedId);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["udgspz-entities", "country-links", selectedId] });
      notifyModuleSuccess(toast, "Country-links обновлены");
    },
    onError: (e: Error) => notifyModuleError(toast, "refresh country-links", e),
  });
  const deleteCountryLinkMutation = useMutation({
    mutationFn: async (linkId: number) => {
      if (!selectedId) throw new Error("eudg_id is required");
      return udgspzEntitiesApi.deleteCountryLink(selectedId, linkId);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["udgspz-entities", "country-links", selectedId] });
      notifyModuleSuccess(toast, "Связь удалена");
    },
    onError: (e: Error) => notifyModuleError(toast, "удаление country-link", e),
  });
  const hierarchyQuery = useQuery({
    queryKey: ["udgspz-entities", "hierarchy", deletedFilter],
    queryFn: () =>
      udgspzEntitiesApi.getHierarchy({
        include_deleted: deletedFilter === "include",
        deleted_only: deletedFilter === "deleted_only",
      }),
  });
  const softDeleteMutation = useMutation({
    mutationFn: async () => {
      if (!selectedId) throw new Error("eudg_id is required");
      return udgspzEntitiesApi.softDelete(selectedId);
    },
    onSuccess: async () => {
      setDeleteConfirmOpen(false);
      await queryClient.invalidateQueries({ queryKey: ["udgspz-entities", "list"] });
      await queryClient.invalidateQueries({ queryKey: ["udgspz-entities", "hierarchy"] });
      setSelectedId(null);
      notifyModuleSuccess(toast, "Сущность УДГСПЗ удалена");
    },
    onError: (e: Error) => notifyModuleError(toast, "удаление сущности УДГСПЗ", e),
  });
  const restoreMutation = useMutation({
    mutationFn: async () => {
      if (!selectedId) throw new Error("eudg_id is required");
      return udgspzEntitiesApi.restore(selectedId);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["udgspz-entities"] });
      notifyModuleSuccess(toast, "Сущность УДГСПЗ восстановлена");
    },
    onError: (e: Error) => notifyModuleError(toast, "восстановление сущности УДГСПЗ", e),
  });
  const createEntityMutation = useMutation({
    mutationFn: async (payload: {
      eudg_name: string;
      kls_id_eudg_type: number;
      eudg_id_parent?: number | null;
      eudg_valid_from?: string | null;
      eudg_valid_to?: string | null;
    }) => udgspzEntitiesApi.create(payload),
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: ["udgspz-entities", "list"] });
      await queryClient.invalidateQueries({ queryKey: ["udgspz-entities", "heatmap-points"] });
      setSelectedId(created.eudg_id);
      setActiveTab("general");
      setCreateNameInput("");
      setCreateParentIdInput("none");
      setCreateValidFrom("");
      setCreateValidTo("");
      setCreateDialogOpen(false);
      notifyModuleSuccess(toast, "Сущность УДГСПЗ создана");
    },
    onError: (e: Error) => {
      notifyModuleError(toast, "создание сущности УДГСПЗ", e);
    },
  });
  const uploadMaterialMutation = useMutation({
    mutationFn: async (file: File) => {
      if (!selectedId) throw new Error("eudg_id is required");
      return udgspzEntitiesApi.uploadMaterial(selectedId, file);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["udgspz-entities", "materials", selectedId] });
      notifyModuleSuccess(toast, "Файл загружен");
    },
    onError: (e: Error) => {
      notifyModuleError(toast, "загрузку материала УДГСПЗ", e);
    },
  });
  const deleteMaterialMutation = useMutation({
    mutationFn: async (udgFileId: number) => {
      if (!selectedId) throw new Error("eudg_id is required");
      return udgspzEntitiesApi.deleteMaterial(selectedId, udgFileId);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["udgspz-entities", "materials", selectedId] });
      notifyModuleSuccess(toast, "Материал УДГСПЗ удален");
    },
    onError: (e: Error) => {
      notifyModuleError(toast, "удаление материала УДГСПЗ", e);
    },
  });
  const downloadMaterial = async (udgFileId: number, filename?: string | null) => {
    if (!selectedId) return;
    try {
      const blob = await udgspzEntitiesApi.downloadMaterialBlob(selectedId, udgFileId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename?.trim() || `udgspz-material-${udgFileId}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      notifyModuleError(toast, "скачивание материала УДГСПЗ", e as Error);
    }
  };

  useEffect(() => {
    setNameInput(detailQuery.data?.eudg_name || "");
    setTypeIdInput(
      detailQuery.data?.kls_id_eudg_type != null ? String(detailQuery.data.kls_id_eudg_type) : "none",
    );
    setParentIdInput(detailQuery.data?.eudg_id_parent != null ? String(detailQuery.data.eudg_id_parent) : "none");
    setValidFromInput(detailQuery.data?.eudg_valid_from ?? "");
    setValidToInput(detailQuery.data?.eudg_valid_to ?? "");
  }, [detailQuery.data]);

  useEffect(() => {
    if (createTypeIdInput !== "none") return;
    const defaultType = metaQuery.data?.eudg_type_options?.[0];
    if (defaultType) {
      setCreateTypeIdInput(String(defaultType.kls_id));
    }
  }, [createTypeIdInput, metaQuery.data?.eudg_type_options]);

  return (
    <>
    <MainWorkspaceLayout
      header={
        <Header
          className="sticky top-0 z-50"
          onSearch={(query) => {
            setSearchTerm(query);
            setSearchParams(
              (prev) => {
                const next = new URLSearchParams(prev);
                if (query) next.set("q", query);
                else next.delete("q");
                return next;
              },
              { replace: true },
            );
          }}
        />
      }
      sidebar={
        <EntityListPanel
          title={
            <ModuleRegistryTitle title="УДГСПЗ" helpLabel="Справка по реестру УДГСПЗ">
              <p className="font-medium">Зачем</p>
              <p>Реестр сущностей УДГСПЗ: найти запись и открыть карточку на карте справа.</p>
              <p className="font-medium mt-2">Как работать</p>
              <ul className="list-disc pl-4 space-y-1">
                <li>Поиск по имени; клик по строке открывает карточку</li>
                <li>Иконка фильтра — статус, период и тепловая карта</li>
                <li>«+» — диалог новой сущности; корзина — soft-delete выбранной</li>
              </ul>
            </ModuleRegistryTitle>
          }
          searchValue={searchTerm}
          onSearchChange={setSearchTerm}
          searchPlaceholder="Поиск по сущностям УДГСПЗ..."
          headerActions={
            <>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    size="icon"
                    variant="outline"
                    className="relative h-7 w-7"
                    title="Фильтры списка"
                    aria-label="Фильтры списка"
                  >
                    <ListFilter className="h-3.5 w-3.5" />
                    {listFiltersActive ? (
                      <span
                        className="absolute right-0.5 top-0.5 h-1.5 w-1.5 rounded-full bg-primary"
                        aria-hidden
                      />
                    ) : null}
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  align="end"
                  className="z-[230] w-[min(100vw-1.5rem,24rem)] space-y-3 p-3"
                >
                  <div className="flex items-center gap-1.5">
                    <p className="text-sm font-medium text-foreground">Фильтры списка</p>
                    <SectionHelpTooltip label="Справка по фильтрам УДГСПЗ" side="bottom">
                      <p className="font-medium">Зачем</p>
                      <p>
                        Отобрать список и тепловую карту, не занимая место формой новой сущности.
                      </p>
                      <p className="font-medium mt-2">Как работать</p>
                      <ul className="list-disc pl-4 space-y-1">
                        <li>Статус и период действуют на список и тепловую карту</li>
                        <li>«+» у заголовка реестра — диалог: имя, тип, родитель и срок действия</li>
                        <li>Поиск по имени — в списке слева</li>
                        <li>Корзина — soft-delete выбранной записи</li>
                      </ul>
                    </SectionHelpTooltip>
                  </div>
                  <p className="text-[11px] text-muted-foreground">
                    Статус и период действуют на список и тепловую карту.
                  </p>
                  <div className="space-y-1">
                    <Label className="text-xs">Статус записей</Label>
                    <Select
                      value={deletedFilter}
                      onValueChange={(v) => setDeletedFilter(v as "active" | "include" | "deleted_only")}
                    >
                      <SelectTrigger className="h-8 w-full">
                        <SelectValue placeholder="Статус" />
                      </SelectTrigger>
                      <SelectContent className="z-[250]">
                        <SelectItem value="active">Только активные</SelectItem>
                        <SelectItem value="include">Включая удалённые</SelectItem>
                        <SelectItem value="deleted_only">Только удалённые</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Период</Label>
                    <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-1.5">
                      <Input
                        type="date"
                        className="h-8 min-w-0 w-full"
                        aria-label="Период с"
                        value={validAtFrom}
                        onChange={(e) => setValidAtFrom(e.target.value)}
                      />
                      <span className="shrink-0 text-xs text-muted-foreground">—</span>
                      <Input
                        type="date"
                        className="h-8 min-w-0 w-full"
                        aria-label="Период по"
                        value={validAtTo}
                        onChange={(e) => setValidAtTo(e.target.value)}
                      />
                    </div>
                  </div>
                  <label className="flex items-center gap-1.5 text-xs text-foreground">
                    <Checkbox
                      checked={heatmapEnabled}
                      onCheckedChange={(v) => setHeatmapEnabled(v === true)}
                    />
                    Тепловая карта
                  </label>
                </PopoverContent>
              </Popover>
              <Button
                type="button"
                size="icon"
                variant="outline"
                className="h-7 w-7"
                title="Удалить выбранную сущность"
                aria-label="Удалить выбранную сущность"
                disabled={selectedId == null || isSelectedDeleted || softDeleteMutation.isPending}
                onClick={() => {
                  if (selectedId == null || isSelectedDeleted) return;
                  setDeleteConfirmOpen(true);
                }}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
              <Button
                type="button"
                size="icon"
                className="h-7 w-7"
                title="Добавить сущность"
                aria-label="Добавить сущность"
                onClick={() => setCreateDialogOpen(true)}
              >
                <Plus className="h-3.5 w-3.5" />
              </Button>
            </>
          }
          isLoading={listQuery.isLoading}
          isError={listQuery.isError}
          errorText="Ошибка загрузки реестра УДГСПЗ. Проверьте доступ и API."
          emptyText="Реестр УДГСПЗ пуст: записи не найдены или недоступны по фильтру."
          items={entities}
          selectedId={selectedId}
          getId={(item) => item.eudg_id}
          onSelect={setSelectedId}
          renderItem={(item) => (
            <>
              <div className="text-sm font-medium truncate">
                {item.eudg_name}
                {item.is_del ? (
                  <span className="ml-2 text-xs font-normal text-destructive">удалено</span>
                ) : null}
              </div>
              <div className="text-xs text-muted-foreground mt-1 truncate">
                Тип: {item.eudg_type_name || `#${item.kls_id_eudg_type}`}
              </div>
            </>
          )}
        />
      }
      workspace={
        <div className="absolute inset-0 min-h-0 bg-background">
          <ResizablePanelGroup
            direction="horizontal"
            autoSaveId="udgspz-workspace-map-split"
            className="h-full w-full"
          >
            <ResizablePanel defaultSize={60} minSize={28} className="min-h-0 min-w-0">
              <div className="h-full min-h-0 overflow-auto p-4">
          {isSelectedDeleted ? (
            <Card className="max-w-2xl">
              <CardHeader>
                <CardTitle>
                  {selectedEntity?.eudg_name || `#${selectedId}`} · удалена
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm text-muted-foreground">
                <p>Сущность soft-deleted. Редактирование недоступно до восстановления.</p>
                <Button
                  disabled={restoreMutation.isPending}
                  onClick={() => restoreMutation.mutate()}
                >
                  {restoreMutation.isPending ? "Восстановление…" : "Восстановить"}
                </Button>
              </CardContent>
            </Card>
          ) : (
          <EntityDetailsShell
            selected={selectedId != null}
            selectedTitle={selectedEntity?.eudg_name || (selectedId != null ? `#${selectedId}` : undefined)}
            emptyTitle="Выберите сущность УДГСПЗ"
            emptyDescription="Слева доступен реестр УДГСПЗ. Откройте запись для просмотра карточки."
            activeTab={activeTab}
            onTabChange={setActiveTab}
            tabs={[
              {
                value: "general",
                label: "Общие сведения",
                content: (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Карточка УДГСПЗ (MVP)</CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm text-muted-foreground">
                      {detailQuery.isLoading ? (
                        <div>Загрузка данных...</div>
                      ) : detailQuery.data ? (
                        <div className="space-y-3">
                          <div>
                            <span className="text-foreground font-medium">Наименование:</span>{" "}
                            {detailQuery.data.eudg_name}
                          </div>
                          <div>
                            <span className="text-foreground font-medium">Тип:</span>{" "}
                            {detailQuery.data.eudg_type_name || `#${detailQuery.data.kls_id_eudg_type}`}
                          </div>
                          <div>
                            <span className="text-foreground font-medium">Родитель ID:</span>{" "}
                            {detailQuery.data.eudg_id_parent ?? "-"}
                          </div>
                          <div>
                            <span className="text-foreground font-medium">Геометрий:</span>{" "}
                            {detailQuery.data.map_geometry_count}
                          </div>
                          <div>
                            <span className="text-foreground font-medium">Окно действия:</span>{" "}
                            {detailQuery.data.eudg_valid_from || "—"} …{" "}
                            {detailQuery.data.eudg_valid_to || "—"}
                          </div>
                          <div className="grid gap-2 md:grid-cols-[1fr_auto]">
                            <div>
                              <Label>Изменить наименование</Label>
                              <Input
                                value={nameInput}
                                onChange={(e) => setNameInput(e.target.value)}
                                placeholder={detailQuery.data.eudg_name || "Введите наименование"}
                              />
                            </div>
                            <div className="flex items-end">
                              <Button
                                onClick={() =>
                                  patchMutation.mutate({
                                    eudg_name: nameInput || null,
                                    kls_id_eudg_type: typeIdInput !== "none" ? Number(typeIdInput) : null,
                                    eudg_id_parent: parentIdInput !== "none" ? Number(parentIdInput) : null,
                                    eudg_valid_from: validFromInput || null,
                                    eudg_valid_to: validToInput || null,
                                  })
                                }
                                disabled={patchMutation.isPending}
                              >
                                {patchMutation.isPending ? "Сохранение..." : "Сохранить"}
                              </Button>
                            </div>
                          </div>
                          <div className="grid gap-3 md:grid-cols-2">
                            <div className="space-y-1">
                              <Label>Действует с</Label>
                              <Input
                                type="date"
                                value={validFromInput}
                                onChange={(e) => setValidFromInput(e.target.value)}
                              />
                            </div>
                            <div className="space-y-1">
                              <Label>Действует по</Label>
                              <Input
                                type="date"
                                value={validToInput}
                                onChange={(e) => setValidToInput(e.target.value)}
                              />
                            </div>
                          </div>
                          <div className="grid gap-3 md:grid-cols-2">
                            <div className="space-y-1">
                              <Label>Тип УДГСПЗ (KLS)</Label>
                              <Select value={typeIdInput} onValueChange={setTypeIdInput}>
                                <SelectTrigger>
                                  <SelectValue placeholder="Не выбран" />
                                </SelectTrigger>
                                <SelectContent className="z-[250]">
                                  <SelectItem value="none">Не выбран</SelectItem>
                                  {(metaQuery.data?.eudg_type_options ?? []).map((option) => (
                                    <SelectItem key={option.kls_id} value={String(option.kls_id)}>
                                      {option.kls_names}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-1">
                              <Label>Родитель</Label>
                              <Select value={parentIdInput} onValueChange={setParentIdInput}>
                                <SelectTrigger>
                                  <SelectValue placeholder="Без родителя" />
                                </SelectTrigger>
                                <SelectContent className="z-[250]">
                                  <SelectItem value="none">Без родителя</SelectItem>
                                  {(metaQuery.data?.parent_options ?? [])
                                    .filter((option) => option.eudg_id !== selectedId)
                                    .map((option) => (
                                      <SelectItem key={option.eudg_id} value={String(option.eudg_id)}>
                                        {option.eudg_name}
                                      </SelectItem>
                                    ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          <div className="border-t border-border pt-4 space-y-3">
                            <div className="text-foreground font-medium">Поля по шаблону</div>
                            {cardQuery.isLoading ? (
                              <div>Загрузка шаблона...</div>
                            ) : cardQuery.data?.template_resolved ? (
                              <>
                                <div className="text-xs text-muted-foreground">
                                  Шаблон: {cardQuery.data.template?.name ?? cardQuery.data.template?.code}
                                </div>
                                <div className="space-y-2">
                                  {cardQuery.data.field_values.map((fv) => (
                                    <div key={fv.field_key} className="space-y-1">
                                      <Label>
                                        {fv.label}{" "}
                                        <span className="text-muted-foreground font-normal">({fv.field_key})</span>
                                      </Label>
                                      <Input
                                        value={templateFieldDrafts[fv.field_key] ?? ""}
                                        readOnly={fv.is_readonly}
                                        onChange={(e) =>
                                          setTemplateFieldDrafts((prev) => ({
                                            ...prev,
                                            [fv.field_key]: e.target.value,
                                          }))
                                        }
                                      />
                                    </div>
                                  ))}
                                </div>
                                <Button
                                  size="sm"
                                  disabled={putCardMutation.isPending}
                                  onClick={() => {
                                    const values: Record<string, unknown> = {};
                                    for (const fv of cardQuery.data?.field_values ?? []) {
                                      if (fv.is_readonly) continue;
                                      const raw = templateFieldDrafts[fv.field_key] ?? "";
                                      values[fv.field_key] = raw === "" ? null : raw;
                                    }
                                    putCardMutation.mutate(values);
                                  }}
                                >
                                  {putCardMutation.isPending ? "Сохранение..." : "Сохранить поля шаблона"}
                                </Button>
                              </>
                            ) : (
                              <div className="text-xs">{cardQuery.data?.message ?? "Шаблон не найден"}</div>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div>Данные сущности не найдены</div>
                      )}
                    </CardContent>
                  </Card>
                ),
              },
              {
                value: "geometries",
                label: "Геометрии",
                content: (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-1.5 text-base">
                        Геометрии УДГСПЗ
                        <SectionHelpTooltip label="Справка: геометрии УДГСПЗ">
                          <p className="font-medium text-foreground">Зачем раздел</p>
                          <p>
                            Привязка контуров сущности к <code className="text-foreground">core.geometries</code>,
                            рисование на карте модуля и настройка стиля (сущность по умолчанию и override
                            геометрии).
                          </p>
                          <p className="font-medium text-foreground">Как работать</p>
                          <ul className="list-disc space-y-1 pl-4">
                            <li>
                              <span className="text-foreground">Нарисовать на карте</span> — новый контур
                              выбранной сущности.
                            </li>
                            <li>
                              <span className="text-foreground">Граница территории</span> — копия контура
                              АТД, ФО, ВО или континента в схему. Повтор той же единицы отклоняется.
                            </li>
                            <li>
                              <span className="text-foreground">Стиль сущности по умолчанию</span> — для
                              привязок без своего override (раскрыть кнопку со сводкой цветов).
                            </li>
                            <li>
                              Поля имени и ID + <span className="text-foreground">Добавить</span> — ручная
                              привязка; клик по объекту ОИ на карте подставляет ID в форму (контур не
                              сохраняется сам).
                            </li>
                            <li>
                              Клик по строке списка или контуру на карте — выбор геометрии для стиля и
                              подсветка.
                            </li>
                            <li>
                              <span className="text-foreground">На карте</span> — показать контур (fit
                              камеры).
                            </li>
                            <li>
                              <span className="text-foreground">Снять подсветку</span> — кнопка у формы
                              стиля, повторный клик по той же геометрии/контуру или клик по пустому месту
                              карты; форма стиля при этом остаётся открытой.
                            </li>
                          </ul>
                          <p className="font-medium text-foreground">Клавиатура</p>
                          <ul className="list-disc space-y-1 pl-4">
                            <li>
                              <kbd className="rounded border border-border bg-muted px-1 font-mono text-[10px]">
                                Esc
                              </kbd>{" "}
                              — снять подсветку на карте.
                            </li>
                            <li>
                              Повторный{" "}
                              <kbd className="rounded border border-border bg-muted px-1 font-mono text-[10px]">
                                Esc
                              </kbd>{" "}
                              (подсветки уже нет) — закрыть форму стиля геометрии.
                            </li>
                          </ul>
                        </SectionHelpTooltip>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm text-muted-foreground space-y-2">
                      <Collapsible
                        open={entityStyleOpen}
                        onOpenChange={setEntityStyleOpen}
                        className="space-y-2"
                      >
                        <div className="flex flex-wrap items-center gap-2">
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="h-8 shrink-0"
                            disabled={selectedId == null || isSelectedDeleted}
                            onClick={() => setUdgMapDrawing(true)}
                          >
                            <Crosshair className="mr-1 h-4 w-4" />
                            Нарисовать на карте
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="h-8 shrink-0"
                            disabled={selectedId == null || isSelectedDeleted}
                            onClick={() => setGeographyOpen(true)}
                          >
                            <MapPinned className="mr-1 h-4 w-4" />
                            Граница территории
                          </Button>
                          <UdgspzGeographyAttachDialog
                            open={geographyOpen}
                            onOpenChange={setGeographyOpen}
                            pending={attachGeographyMutation.isPending}
                            onAttach={(unit) => attachGeographyMutation.mutate(unit.id)}
                          />
                          <CollapsibleTrigger asChild>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="h-8 min-w-0 flex-1 justify-start gap-2 border-dashed px-2.5 text-xs font-medium"
                              disabled={selectedId == null}
                            >
                              <MapStylePreviewChips
                                fillColor={entityDefaultFill}
                                lineColor={entityDefaultLine}
                                lineWidth={entityDefaultWidth}
                                lineDashCode={entityDefaultDash}
                                baselineWidth={2}
                                title={`Заливка ${entityDefaultFill} · обводка ${entityDefaultLine}`}
                              />
                              <span className="truncate">Стиль сущности по умолчанию</span>
                              <span className="hidden truncate font-normal text-muted-foreground sm:inline">
                                · без override
                              </span>
                              <ChevronDown
                                className={`ml-auto h-3.5 w-3.5 shrink-0 transition-transform ${
                                  entityStyleOpen ? "rotate-180" : ""
                                }`}
                              />
                            </Button>
                          </CollapsibleTrigger>
                        </div>
                        <CollapsibleContent className="rounded-md border border-dashed px-3 pb-3 pt-2">
                          <UdgspzEntityMapStyleForm
                            disabled={selectedId == null || isSelectedDeleted}
                            title="Пустые поля наследуют тип KLS. Стиль действует для привязок без своего override."
                            clearLabel="Сбросить к типу"
                            fillColor={entityDefaultFill}
                            lineColor={entityDefaultLine}
                            patternCode={entityDefaultPattern}
                            lineWidth={entityDefaultWidth}
                            lineDashCode={entityDefaultDash}
                            labelVisible={entityDefaultLabelVisible}
                            labelTextMode={entityDefaultLabelTextMode}
                            labelText={entityDefaultLabelText}
                            labelColor={entityDefaultLabelColor}
                            labelSize={entityDefaultLabelSize}
                            labelTextAnchor={entityDefaultLabelAnchor}
                            labelOffsetX={entityDefaultLabelOffsetX}
                            labelOffsetY={entityDefaultLabelOffsetY}
                            labelHaloColor={entityDefaultLabelHaloColor}
                            labelHaloWidth={entityDefaultLabelHaloWidth}
                            patterns={metaQuery.data?.map_pattern_options ?? []}
                            dashOptions={metaQuery.data?.map_dash_options ?? []}
                            textModeOptions={metaQuery.data?.map_label_text_mode_options}
                            textAnchorOptions={metaQuery.data?.map_label_text_anchor_options}
                            onSave={async (payload) => {
                              if (selectedId == null) return;
                              try {
                                await udgspzEntitiesApi.updateEntityMapStyle(selectedId, payload);
                                await queryClient.invalidateQueries({
                                  queryKey: ["udgspz-entities", "detail", selectedId],
                                });
                                await queryClient.invalidateQueries({
                                  queryKey: ["udgspz-entities", "map-contours"],
                                });
                                notifyModuleSuccess(toast, "Стиль сущности сохранён");
                              } catch (error) {
                                notifyModuleError(toast, "сохранение стиля УДГСПЗ", error as Error);
                                throw error;
                              }
                            }}
                            onClear={async () => {
                              if (selectedId == null) return;
                              try {
                                await udgspzEntitiesApi.updateEntityMapStyle(selectedId, { clear: true });
                                await queryClient.invalidateQueries({
                                  queryKey: ["udgspz-entities", "detail", selectedId],
                                });
                                await queryClient.invalidateQueries({
                                  queryKey: ["udgspz-entities", "map-contours"],
                                });
                                notifyModuleSuccess(toast, "Стиль сущности сброшен к типу");
                              } catch (error) {
                                notifyModuleError(toast, "сброс стиля УДГСПЗ", error as Error);
                                throw error;
                              }
                            }}
                          />
                        </CollapsibleContent>
                      </Collapsible>
                      <p className="text-xs">
                        Привязка к <code className="text-foreground">core.geometries.id</code>. Клик по объекту
                        ОИ на карте подставляет geometry id в форму и не сохраняет контур.
                      </p>
                      <EntityGeometrySection<UdgspzMapGeometryItem>
                        addNameValue={bindGeometryName}
                        onAddNameChange={setBindGeometryName}
                        addGeometryIdValue={bindGeometryId}
                        onAddGeometryIdChange={setBindGeometryId}
                        onAdd={() => {
                          const id = Number(bindGeometryId);
                          if (!Number.isFinite(id) || id < 1) {
                            notifyModuleError(
                              toast,
                              "привязку геометрии УДГСПЗ",
                              new Error("Укажите корректный ID"),
                            );
                            return;
                          }
                          createMapGeometryMutation.mutate(id);
                        }}
                        addPending={createMapGeometryMutation.isPending}
                        isLoading={detailQuery.isLoading}
                        items={mapGeometries}
                        getKey={(item) => item.eudg_map_geom_id}
                        renderName={(item) =>
                          item.geometry_name?.trim() ||
                          `Геометрия #${item.map_geometry_id ?? item.eudg_map_geom_id}`
                        }
                        renderGeometryId={(item) => item.map_geometry_id}
                        renderStylePreview={(item) => {
                          const fill = item.map_fill_color ?? entityDefaultFill;
                          const line = item.map_line_color ?? entityDefaultLine;
                          const width = item.map_line_width ?? entityDefaultWidth;
                          const dash = item.map_line_dash_code ?? entityDefaultDash;
                          const pattern = item.map_pattern_code ?? entityDefaultPattern;
                          const hasOverride = Boolean(
                            item.map_fill_color ||
                              item.map_line_color ||
                              item.map_pattern_code ||
                              item.map_line_width != null ||
                              item.map_line_dash_code ||
                              item.map_label_visible != null ||
                              item.map_label_text_mode ||
                              item.map_label_text ||
                              item.map_label_color ||
                              item.map_label_size != null ||
                              item.map_label_text_anchor ||
                              item.map_label_offset_x != null ||
                              item.map_label_offset_y != null ||
                              item.map_label_halo_color ||
                              item.map_label_halo_width != null,
                          );
                          const labelHidden =
                            (item.map_label_visible ?? entityDefaultLabelVisible) === false;
                          const labelCustom =
                            (item.map_label_text_mode ?? entityDefaultLabelTextMode) === "custom";
                          const dashLabel = dash === "dashed" ? "пунктир" : "сплошная";
                          const title = [
                            `Заливка ${fill}`,
                            `обводка ${line}`,
                            `толщина ${width}`,
                            dashLabel,
                            pattern === "hatch" ? "штриховка" : "заливка сплошная",
                            labelHidden ? "подпись скрыта" : labelCustom ? "свой текст подписи" : "подпись",
                            hasOverride ? "свой override" : "наследует сущность/тип",
                          ].join(" · ");
                          return (
                            <MapStylePreviewChips
                              fillColor={fill}
                              lineColor={line}
                              lineWidth={width}
                              lineDashCode={dash}
                              baselineWidth={Number(entityDefaultWidth) || 2}
                              hasOverride={hasOverride}
                              title={title}
                            />
                          );
                        }}
                        selectedKey={selectedMapGeomId}
                        onSelect={(item) => toggleMapGeomSelection(item.eudg_map_geom_id)}
                        onFocus={async (item) => {
                          if (item.map_geometry_id == null) return;
                          try {
                            const geometry = await udgspzEntitiesApi.getGeometryById(item.map_geometry_id);
                            if (!geometry?.geojson) {
                              notifyModuleError(
                                toast,
                                "фокус геометрии УДГСПЗ на карте",
                                new Error("У геометрии нет GeoJSON"),
                              );
                              return;
                            }
                            const focusOnMap = () => {
                              window.dispatchEvent(
                                new CustomEvent("focusGeometryOnMap", {
                                  detail: { geometry, source: "highlight" },
                                }),
                              );
                            };
                            if (mapCollapsed) {
                              mapPanelRef.current?.expand();
                              setMapCollapsed(false);
                              setMapPreviewGeometry(geometry);
                              setMapPreviewNonce((n) => n + 1);
                            } else {
                              focusOnMap();
                              // После select появится форма стиля → resize панели; повторяем fit после layout.
                              requestAnimationFrame(() => {
                                requestAnimationFrame(focusOnMap);
                              });
                            }
                            selectMapGeomForEdit(item.eudg_map_geom_id);
                            notifyModuleSuccess(toast, "Геометрия на карте модуля");
                          } catch (error) {
                            notifyModuleError(toast, "фокус геометрии УДГСПЗ на карте", error);
                          }
                        }}
                        onDelete={(item) => deleteMapGeometryMutation.mutate(item.eudg_map_geom_id)}
                        deletePending={deleteMapGeometryMutation.isPending}
                        emptyText="Геометрии не привязаны"
                      />
                      {selectedMapGeomId == null ? (
                        <p className="text-xs text-muted-foreground">
                          Выберите геометрию в списке или кликните контур на карте, чтобы задать её стиль.
                          Повторный клик или Esc снимает подсветку на карте.
                        </p>
                      ) : (
                        <div className="space-y-2">
                          {mapHighlightGeomId === selectedMapGeomId ? (
                            <div className="flex flex-wrap items-center gap-2">
                              <p className="text-xs text-muted-foreground">
                                На карте подсвечена эта геометрия — так сложнее оценить пунктир и толщину.
                              </p>
                              <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                className="h-7 px-2 text-xs"
                                onClick={() => setMapHighlightGeomId(null)}
                              >
                                Снять подсветку
                              </Button>
                            </div>
                          ) : (
                            <p className="text-xs text-muted-foreground">
                              Подсветка снята — на карте виден стиль контура. Esc закроет форму.
                            </p>
                          )}
                          <UdgspzEntityMapStyleForm
                            disabled={selectedId == null || isSelectedDeleted}
                            title={`Стиль геометрии #${selectedMapGeom?.map_geometry_id ?? selectedMapGeomId}. Пустые поля наследуют сущность, затем тип.`}
                            clearLabel="Сбросить к сущности"
                            fillColor={selectedMapGeom?.map_fill_color ?? entityDefaultFill}
                            lineColor={selectedMapGeom?.map_line_color ?? entityDefaultLine}
                            patternCode={selectedMapGeom?.map_pattern_code ?? entityDefaultPattern}
                            lineWidth={selectedMapGeom?.map_line_width ?? entityDefaultWidth}
                            lineDashCode={selectedMapGeom?.map_line_dash_code ?? entityDefaultDash}
                            labelVisible={
                              selectedMapGeom?.map_label_visible ?? entityDefaultLabelVisible
                            }
                            labelTextMode={
                              selectedMapGeom?.map_label_text_mode ?? entityDefaultLabelTextMode
                            }
                            labelText={
                              selectedMapGeom?.map_label_text ?? entityDefaultLabelText
                            }
                            labelColor={selectedMapGeom?.map_label_color ?? entityDefaultLabelColor}
                            labelSize={selectedMapGeom?.map_label_size ?? entityDefaultLabelSize}
                            labelTextAnchor={
                              selectedMapGeom?.map_label_text_anchor ?? entityDefaultLabelAnchor
                            }
                            labelOffsetX={
                              selectedMapGeom?.map_label_offset_x ?? entityDefaultLabelOffsetX
                            }
                            labelOffsetY={
                              selectedMapGeom?.map_label_offset_y ?? entityDefaultLabelOffsetY
                            }
                            labelHaloColor={
                              selectedMapGeom?.map_label_halo_color ?? entityDefaultLabelHaloColor
                            }
                            labelHaloWidth={
                              selectedMapGeom?.map_label_halo_width ?? entityDefaultLabelHaloWidth
                            }
                            patterns={metaQuery.data?.map_pattern_options ?? []}
                            dashOptions={metaQuery.data?.map_dash_options ?? []}
                            textModeOptions={metaQuery.data?.map_label_text_mode_options}
                            textAnchorOptions={metaQuery.data?.map_label_text_anchor_options}
                            onSave={async (payload) => {
                              if (selectedId == null || selectedMapGeomId == null) return;
                              try {
                                await udgspzEntitiesApi.updateGeometryMapStyle(
                                  selectedId,
                                  selectedMapGeomId,
                                  payload,
                                );
                                await queryClient.invalidateQueries({
                                  queryKey: ["udgspz-entities", "detail", selectedId],
                                });
                                await queryClient.invalidateQueries({
                                  queryKey: ["udgspz-entities", "map-contours"],
                                });
                                setMapHighlightGeomId(null);
                                notifyModuleSuccess(toast, "Стиль геометрии сохранён");
                              } catch (error) {
                                notifyModuleError(toast, "сохранение стиля геометрии УДГСПЗ", error as Error);
                                throw error;
                              }
                            }}
                            onClear={async () => {
                              if (selectedId == null || selectedMapGeomId == null) return;
                              try {
                                await udgspzEntitiesApi.updateGeometryMapStyle(
                                  selectedId,
                                  selectedMapGeomId,
                                  { clear: true },
                                );
                                await queryClient.invalidateQueries({
                                  queryKey: ["udgspz-entities", "detail", selectedId],
                                });
                                await queryClient.invalidateQueries({
                                  queryKey: ["udgspz-entities", "map-contours"],
                                });
                                setMapHighlightGeomId(null);
                                notifyModuleSuccess(toast, "Стиль геометрии сброшен к сущности");
                              } catch (error) {
                                notifyModuleError(toast, "сброс стиля геометрии УДГСПЗ", error as Error);
                                throw error;
                              }
                            }}
                          />
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ),
              },
              {
                value: "resolve",
                label: "Resolve",
                content: (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Resolve-region</CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm text-muted-foreground space-y-3">
                      <p className="text-xs">
                        Точка lon/lat → сущности УДГСПЗ, чьи привязанные контуры содержат точку.
                      </p>
                      <div className="grid gap-2 md:grid-cols-[1fr_1fr_auto]">
                        <div className="space-y-1">
                          <Label>Longitude</Label>
                          <Input value={resolveLon} onChange={(e) => setResolveLon(e.target.value)} />
                        </div>
                        <div className="space-y-1">
                          <Label>Latitude</Label>
                          <Input value={resolveLat} onChange={(e) => setResolveLat(e.target.value)} />
                        </div>
                        <div className="flex items-end">
                          <Button
                            size="sm"
                            disabled={resolveRegionMutation.isPending}
                            onClick={() => resolveRegionMutation.mutate()}
                          >
                            {resolveRegionMutation.isPending ? "Поиск..." : "Найти"}
                          </Button>
                        </div>
                      </div>
                      <div className="space-y-1">
                        {(resolveRegionMutation.data?.items ?? []).length === 0 ? (
                          <div className="text-xs">Нет попаданий (или запрос ещё не выполнялся)</div>
                        ) : (
                          resolveRegionMutation.data?.items.map((hit) => (
                            <button
                              key={hit.eudg_id}
                              type="button"
                              className="block w-full rounded-md border border-border px-2 py-1.5 text-left hover:bg-muted/40"
                              onClick={() => {
                                setSelectedId(hit.eudg_id);
                                setActiveTab("general");
                              }}
                            >
                              <div className="text-foreground">{hit.eudg_name}</div>
                              <div className="text-xs">
                                #{hit.eudg_id} · {hit.eudg_type_name || hit.kls_id_eudg_type}
                              </div>
                            </button>
                          ))
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ),
              },
              {
                value: "bridges",
                label: "Мосты",
                content: (
                  <div className="space-y-3">
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-base">Infra links (ОИ внутри региона)</CardTitle>
                      </CardHeader>
                      <CardContent className="text-sm text-muted-foreground space-y-2">
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={!selectedId || refreshInfraLinksMutation.isPending}
                          onClick={() => refreshInfraLinksMutation.mutate()}
                        >
                          Spatial refresh
                        </Button>
                        {(infraLinksQuery.data?.items ?? []).length === 0 ? (
                          <div className="text-xs">Нет связанных объектов ОИ</div>
                        ) : (
                          <ul className="space-y-1">
                            {infraLinksQuery.data?.items.map((link) => (
                              <li key={link.id} className="rounded-md border border-border px-2 py-1.5">
                                <div className="text-foreground">
                                  {link.object_name || `object #${link.object_id}`}
                                </div>
                                <div className="text-xs">
                                  #{link.object_id} · {link.relation_type}
                                  {link.is_auto_detected ? " · auto" : ""}
                                </div>
                              </li>
                            ))}
                          </ul>
                        )}
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-base">Country links (ГО ↔ регион)</CardTitle>
                      </CardHeader>
                      <CardContent className="text-sm text-muted-foreground space-y-2">
                        <div className="flex flex-wrap gap-2 items-end">
                          <div className="space-y-1">
                            <Label>ginf_obj_id</Label>
                            <Input
                              value={linkGinfObjId}
                              onChange={(e) => setLinkGinfObjId(e.target.value)}
                            />
                          </div>
                          <Button
                            size="sm"
                            disabled={!selectedId || createCountryLinkMutation.isPending}
                            onClick={() => createCountryLinkMutation.mutate()}
                          >
                            Связать
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={!selectedId || refreshCountryLinksMutation.isPending}
                            onClick={() => refreshCountryLinksMutation.mutate()}
                          >
                            Spatial refresh
                          </Button>
                        </div>
                        {(countryLinksQuery.data?.items ?? []).length === 0 ? (
                          <div className="text-xs">Нет связей с ГО</div>
                        ) : (
                          <ul className="space-y-1">
                            {countryLinksQuery.data?.items.map((link) => (
                              <li
                                key={link.id}
                                className="flex items-center justify-between gap-2 rounded-md border border-border px-2 py-1.5"
                              >
                                <div>
                                  <div className="text-foreground">
                                    {link.ginf_obj_name || `#${link.ginf_obj_id}`}
                                  </div>
                                  <div className="text-xs">
                                    #{link.ginf_obj_id} · {link.coverage}
                                    {link.is_auto_detected ? " · auto" : ""}
                                  </div>
                                </div>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  disabled={deleteCountryLinkMutation.isPending}
                                  onClick={() => deleteCountryLinkMutation.mutate(link.id)}
                                >
                                  Удалить
                                </Button>
                              </li>
                            ))}
                          </ul>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                ),
              },
              {
                value: "hierarchy",
                label: "Иерархия",
                content: (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Иерархия сущностей</CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm text-muted-foreground space-y-2">
                      {hierarchyQuery.isLoading ? (
                        <div>Загрузка...</div>
                      ) : (hierarchyQuery.data?.roots ?? []).length === 0 ? (
                        <div className="text-xs">Дерево пусто</div>
                      ) : (
                        <ul className="space-y-1">
                          {(hierarchyQuery.data?.roots ?? []).map((node) => (
                            <li key={node.eudg_id}>
                              <button
                                type="button"
                                className="text-left text-foreground hover:underline"
                                onClick={() => {
                                  setSelectedId(node.eudg_id);
                                  setActiveTab("general");
                                }}
                              >
                                {node.eudg_name}
                              </button>
                              {node.children.length > 0 && (
                                <ul className="ml-4 mt-1 space-y-1 border-l border-border pl-2">
                                  {node.children.map((child) => (
                                    <li key={child.eudg_id}>
                                      <button
                                        type="button"
                                        className="text-left hover:underline"
                                        onClick={() => {
                                          setSelectedId(child.eudg_id);
                                          setActiveTab("general");
                                        }}
                                      >
                                        {child.eudg_name}
                                      </button>
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                      <div className="text-xs pt-2 border-t border-border">
                        Типов в дереве meta: {metaQuery.data?.eudg_type_tree?.length ?? 0} корневых узлов
                      </div>
                    </CardContent>
                  </Card>
                ),
              },
              {
                value: "materials",
                label: "Материалы",
                content: (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Материалы УДГСПЗ</CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm text-muted-foreground">
                      <EntityFilesSection
                        mode="upload"
                        onUploadFile={(file) => uploadMaterialMutation.mutate(file)}
                        addPending={uploadMaterialMutation.isPending}
                        isLoading={materialsQuery.isLoading}
                        items={materialsQuery.data ?? []}
                        getKey={(item) => item.udg_file_id}
                        renderColumns={(item) => (
                          <div>
                            <div className="text-foreground truncate">
                              {item.name?.trim() ||
                                item.udg_file_desc?.trim() ||
                                `Материал #${item.udg_file_id}`}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {item.size != null ? `${item.size} байт` : "—"}
                              {item.content_type ? ` · ${item.content_type}` : ""}
                            </div>
                          </div>
                        )}
                        onDownload={(item) =>
                          void downloadMaterial(item.udg_file_id, item.name || item.udg_file_desc)
                        }
                        onDelete={(item) => deleteMaterialMutation.mutate(item.udg_file_id)}
                        deletePending={deleteMaterialMutation.isPending}
                      />
                    </CardContent>
                  </Card>
                ),
              },
            ]}
          />
          )}
              </div>
            </ResizablePanel>
            <ResizableHandle withHandle className="w-1.5 bg-border hover:bg-primary/30" />
            <ResizablePanel
              ref={mapPanelRef}
              defaultSize={40}
              minSize={18}
              collapsible
              collapsedSize={4}
              onCollapse={() => setMapCollapsed(true)}
              onExpand={() => setMapCollapsed(false)}
              className="min-h-0 min-w-0"
            >
              <UdgspzWorkspaceMapPanel
                collapsed={mapCollapsed}
                onRequestCollapse={() => mapPanelRef.current?.collapse()}
                onRequestExpand={() => mapPanelRef.current?.expand()}
                contours={contourQuery.data ?? []}
                hiddenTypeIds={hiddenContourTypeIds}
                onHiddenTypeChange={(typeId, visible) => {
                  setHiddenContourTypeIds((current) =>
                    visible ? current.filter((id) => id !== typeId) : [...current, typeId],
                  );
                }}
                onMvtSelect={({ geometryId }) => {
                  if (geometryId == null) return;
                  setActiveTab("geometries");
                  setBindGeometryId(String(geometryId));
                }}
                selectedThematicFeatureId={
                  mapHighlightGeomId != null ? `udgspz-${mapHighlightGeomId}` : null
                }
                onThematicFeatureClick={(featureId) => {
                  const match = /^udgspz-(\d+)$/.exec(featureId);
                  if (!match) return;
                  const geomId = Number(match[1]);
                  if (!Number.isFinite(geomId)) return;
                  const contour = (contourQuery.data ?? []).find(
                    (item) => item.eudg_map_geom_id === geomId,
                  );
                  if (contour) {
                    setSelectedId(contour.eudg_id);
                  }
                  toggleMapGeomSelection(geomId);
                  setActiveTab("geometries");
                }}
                onClearThematicSelection={() => setMapHighlightGeomId(null)}
                previewGeometry={mapPreviewGeometry}
                previewNonce={mapPreviewNonce}
                onPreviewConsumed={() => setMapPreviewGeometry(null)}
                drawingActive={udgMapDrawing}
                onDrawingActiveChange={setUdgMapDrawing}
                canDraw={selectedId != null && !isSelectedDeleted}
                savePending={saveSketchMutation.isPending}
                defaultName={detailQuery.data?.eudg_name ?? ""}
                onSaveDrawn={async (payload) => {
                  await saveSketchMutation.mutateAsync(payload);
                }}
              />
            </ResizablePanel>
          </ResizablePanelGroup>
        </div>
      }
    />
    <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
      <AlertDialogContent className="z-[230]">
        <AlertDialogHeader>
          <AlertDialogTitle>Удалить сущность УДГСПЗ?</AlertDialogTitle>
          <AlertDialogDescription>
            Запись останется в системе и её можно восстановить. Продолжить удаление
            {selectedEntity?.eudg_name ? ` «${selectedEntity.eudg_name}»` : ""}?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={softDeleteMutation.isPending}>Отмена</AlertDialogCancel>
          <AlertDialogAction
            disabled={softDeleteMutation.isPending}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            onClick={(e) => {
              e.preventDefault();
              softDeleteMutation.mutate();
            }}
          >
            {softDeleteMutation.isPending ? "Удаление…" : "Удалить"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
      <DialogContent className="sm:max-w-md z-[220]">
        <DialogHeader>
          <DialogTitle>Новая сущность УДГСПЗ</DialogTitle>
        </DialogHeader>
        <div className="grid gap-3">
          <div className="grid gap-1">
            <Label htmlFor="udgspz-create-name">Наименование</Label>
            <Input
              id="udgspz-create-name"
              value={createNameInput}
              onChange={(e) => setCreateNameInput(e.target.value)}
            />
          </div>
          <div className="grid gap-1">
            <Label>Тип</Label>
            <Select value={createTypeIdInput} onValueChange={setCreateTypeIdInput}>
              <SelectTrigger className="h-8">
                <SelectValue placeholder="Тип УДГСПЗ" />
              </SelectTrigger>
              <SelectContent className="z-[250]">
                <SelectItem value="none">Не выбран</SelectItem>
                {(metaQuery.data?.eudg_type_options ?? []).map((option) => (
                  <SelectItem key={option.kls_id} value={String(option.kls_id)}>
                    {option.kls_names}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-1">
            <Label>Родитель</Label>
            <Select value={createParentIdInput} onValueChange={setCreateParentIdInput}>
              <SelectTrigger className="h-8">
                <SelectValue placeholder="Без родителя" />
              </SelectTrigger>
              <SelectContent className="z-[250]">
                <SelectItem value="none">Без родителя</SelectItem>
                {(metaQuery.data?.parent_options ?? []).map((option) => (
                  <SelectItem key={option.eudg_id} value={String(option.eudg_id)}>
                    {option.eudg_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="grid gap-1">
              <Label htmlFor="udgspz-create-from">Действует с</Label>
              <Input
                id="udgspz-create-from"
                type="date"
                className="h-8"
                value={createValidFrom}
                onChange={(e) => setCreateValidFrom(e.target.value)}
              />
            </div>
            <div className="grid gap-1">
              <Label htmlFor="udgspz-create-to">Действует по</Label>
              <Input
                id="udgspz-create-to"
                type="date"
                className="h-8"
                value={createValidTo}
                onChange={(e) => setCreateValidTo(e.target.value)}
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => setCreateDialogOpen(false)}
            disabled={createEntityMutation.isPending}
          >
            Отмена
          </Button>
          <Button
            type="button"
            disabled={createEntityMutation.isPending || !createNameInput.trim() || createTypeIdInput === "none"}
            onClick={() =>
              createEntityMutation.mutate({
                eudg_name: createNameInput.trim(),
                kls_id_eudg_type: Number(createTypeIdInput),
                eudg_id_parent: createParentIdInput !== "none" ? Number(createParentIdInput) : null,
                eudg_valid_from: createValidFrom || null,
                eudg_valid_to: createValidTo || null,
              })
            }
          >
            {createEntityMutation.isPending ? "Создание..." : "Создать"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}

